import type { AssignmentDetail } from '@repo/types/assignments';

import { format, isAfter, isWithinInterval } from 'date-fns';
import { BookmarkCheckIcon, CalendarIcon, CheckCheckIcon, CheckCircle2Icon, CircleXIcon, ClockIcon, InboxIcon, PencilLineIcon, RepeatIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface AssignmentManageHeaderProps {
  assignment: AssignmentDetail;
  submissionsCount: number;
}

export default function AssignmentManageHeader(assignmentManageHeaderProps: AssignmentManageHeaderProps) {
  const { assignment, submissionsCount } = assignmentManageHeaderProps;
  const isQuizAssignment = assignment.quiz != null;

  return (
    <div className="bg-background flex w-full flex-col gap-4 rounded-2xl border p-4 md:gap-2">
      <div className="flex w-full flex-row items-center gap-4">
        <AvailabilityBadge available={assignment.available} dueTo={assignment.dueTo} />
        <Badge variant={isQuizAssignment ? 'brand' : 'muted'} border className="mx-auto md:mx-0">
          {isQuizAssignment ? (
            <>
              <BookmarkCheckIcon />
              Quiz
            </>
          ) : (
            <>
              <PencilLineIcon />
              Zadanie
            </>
          )}
        </Badge>
      </div>
      <div className="space-y-1">
        <h1 className="text-center text-xl font-bold md:text-start">{assignment.name}</h1>
        {isQuizAssignment && (
          <p className="text-muted-foreground flex items-center justify-center gap-2 text-sm md:justify-start">
            <CheckCircle2Icon className="size-4 shrink-0" />
            To zadanie jest oparte na quizie i jest oceniane automatycznie. Szczegółowe ocenianie odbywa się w widoku quizu.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-4 text-xs text-nowrap md:flex-row md:items-end">
        <div className="flex flex-wrap justify-center gap-2 md:justify-start">
          <Badge variant="muted" border>
            <InboxIcon />
            Odpowiedzi: {submissionsCount}
          </Badge>
          <Badge variant="muted" border>
            <RepeatIcon />
            {assignment.maxAttempts > 0 ? `Podejścia: ${assignment.maxAttempts}` : 'Bez limitu podejść'}
          </Badge>
          {assignment.available && (
            <Badge variant="muted" border>
              <ClockIcon />
              Od {format(assignment.available, 'dd.MM.yyyy HH:mm')}
            </Badge>
          )}
          {assignment.dueTo && (
            <Badge variant="muted" border>
              <CalendarIcon />
              Termin {format(assignment.dueTo, 'dd.MM.yyyy HH:mm')}
            </Badge>
          )}
        </div>

        <Button className={'w-full md:ml-auto md:w-fit'} variant={'outline'} asChild>
          <Link to={`/manage/assignments/builder/${assignment.id}`}>
            <PencilLineIcon /> Edytuj
          </Link>
        </Button>
      </div>
    </div>
  );
}

interface AvailabilityBadgeProps {
  available: string | null;
  dueTo: string | null;
}

function AvailabilityBadge(availabilityBadgeProps: AvailabilityBadgeProps) {
  const { available, dueTo } = availabilityBadgeProps;
  if (
    isWithinInterval(new Date(), {
      start: available || new Date('1000-01-01'),
      end: dueTo || new Date('9999-12-31'),
    })
  ) {
    return (
      <Badge variant={'success'} className="mx-auto md:mx-0">
        <CheckCircle2Icon /> Aktywne
      </Badge>
    );
  }

  if (dueTo && isAfter(new Date(), dueTo)) {
    return (
      <Badge variant={'destructive'} className="mx-auto md:mx-0">
        <CheckCheckIcon /> Po terminie
      </Badge>
    );
  }

  return (
    <Badge variant={'warning'} className="mx-auto md:mx-0">
      <CircleXIcon /> Nie rozpoczęte
    </Badge>
  );
}
